/* Iter51 E25b — Comparative breakdown of sales by product_kind:
 * current month vs previous month, including a withdrawn (anulări) bucket.
 *
 * Uses Recharts BarChart (grouped bars per kind).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { TrendingUp, TrendingDown, Minus, RefreshCw, BarChart3 } from "lucide-react";
import { Button } from "./ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Map raw `kind` keys to display label + chart color.
const KIND_DISPLAY = {
  EE: { label: "EE", color: "#f59e0b" },
  Gaz: { label: "Gaz", color: "#fb923c" },
  VAS: { label: "VAS", color: "#6366f1" },
  HVAS: { label: "HVAS", color: "#a855f7" },
  "Combo EE+VAS": { label: "Combo EE+VAS", color: "#0ea5e9" },
  "Combo Gaz+VAS": { label: "Combo Gaz+VAS", color: "#14b8a6" },
  _withdrawn: { label: "Anulate", color: "#ef4444" },
  unset: { label: "Neclasificat", color: "#94a3b8" },
};

const formatRoMonth = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("ro-RO", { month: "long", year: "numeric" });
};

const ProductKindComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/dashboard/product-kind-comparison`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <Card data-testid="product-kind-comparison-loading">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            Comparație lunară pe tip produs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Se încarcă...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            Comparație lunară pe tip produs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-slate-500 text-sm py-8">
            Nu am putut încărca datele.
            <Button variant="link" onClick={fetchData} className="ml-2">Reîncearcă</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = (data.rows || []).map((row) => {
    const display = KIND_DISPLAY[row.kind] || { label: row.kind, color: "#64748b" };
    return {
      kind: display.label,
      "Luna curentă": row.current,
      "Luna anterioară": row.previous,
      _color: display.color,
      _delta: row.delta,
      _raw: row.kind,
    };
  });

  const totalCurrent = data.current_month?.total_count || 0;
  const totalPrev = data.previous_month?.total_count || 0;
  const totalDelta = totalCurrent - totalPrev;
  const totalDeltaPct = totalPrev > 0 ? Math.round((totalDelta / totalPrev) * 100) : null;

  return (
    <Card data-testid="product-kind-comparison-card">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              Comparație lunară pe tip produs
            </CardTitle>
            <CardDescription className="capitalize">
              {formatRoMonth(data.current_month?.start)} vs {formatRoMonth(data.previous_month?.start)}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} data-testid="comparison-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total summary chip */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900" data-testid="comparison-total-current">
              {totalCurrent.toLocaleString("ro-RO")}
            </span>
            <span className="text-xs text-slate-500">vânzări luna curentă</span>
          </div>
          {totalDeltaPct !== null && (
            <Badge
              className={
                totalDelta > 0
                  ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                  : totalDelta < 0
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-slate-100 text-slate-700 border-slate-200"
              }
              data-testid="comparison-total-delta"
            >
              {totalDelta > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> :
               totalDelta < 0 ? <TrendingDown className="h-3 w-3 mr-1" /> :
               <Minus className="h-3 w-3 mr-1" />}
              {totalDelta > 0 ? "+" : ""}{totalDelta} ({totalDeltaPct > 0 ? "+" : ""}{totalDeltaPct}%)
            </Badge>
          )}
        </div>

        {/* Chart */}
        {chartData.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-8" data-testid="comparison-empty">
            Nu există vânzări în perioadele comparate.
          </div>
        ) : (
          <div className="h-64" data-testid="comparison-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="kind" tick={{ fill: "#475569", fontSize: 11 }} />
                <YAxis tick={{ fill: "#475569", fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                  cursor={{ fill: "#f1f5f9" }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                <Bar dataKey="Luna curentă" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Luna anterioară" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Per-kind delta list (compact) */}
        {chartData.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-slate-100" data-testid="comparison-rows">
            {chartData.map((r) => (
              <div key={r._raw} className="flex items-center justify-between text-xs py-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r._color }} />
                  <span className="text-slate-700">{r.kind}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500 font-mono">
                  <span data-testid={`row-cur-${r._raw}`}>{r["Luna curentă"]}</span>
                  <span className="text-slate-300">/</span>
                  <span data-testid={`row-prev-${r._raw}`}>{r["Luna anterioară"]}</span>
                  <span
                    className={
                      r._delta > 0 ? "text-emerald-600 font-semibold w-12 text-right"
                      : r._delta < 0 ? "text-red-600 font-semibold w-12 text-right"
                      : "text-slate-400 w-12 text-right"
                    }
                  >
                    {r._delta > 0 ? "+" : ""}{r._delta}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductKindComparison;
